import React, { useState } from 'react'
import { 
  useGetExpansionDeliverablesQuery,
  useResolveExpansionDeliverableMutation,
  ExpansionDeliverable
} from '../store/api/expansionDeliverablesApi'
import { formatDateTimeEST } from '../utils/dateUtils'
import { 
  EyeIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'

const ExpansionDeliverables: React.FC = () => {
  const { data: deliverables, isLoading, error, refetch } = useGetExpansionDeliverablesQuery({})
  const [resolveDeliverable] = useResolveExpansionDeliverableMutation()
  const [selectedDeliverable, setSelectedDeliverable] = useState<ExpansionDeliverable | null>(null)
  const [showDetailsModal, setShowDetailsModal] = useState(false)
  const [showResolveModal, setShowResolveModal] = useState(false)
  const [deliverableToResolve, setDeliverableToResolve] = useState<ExpansionDeliverable | null>(null)
  const [seatsToAdd, setSeatsToAdd] = useState<Record<number, number>>({})
  const [invoicePaid, setInvoicePaid] = useState<Record<number, boolean>>({})
  const [invoiceDescription, setInvoiceDescription] = useState<Record<number, string>>({})
  const [resolving, setResolving] = useState<string | null>(null)

  const parseExpansionReasons = (reasons: any) => {
    if (typeof reasons === 'string') {
      try {
        return JSON.parse(reasons)
      } catch {
        return {}
      }
    }
    return reasons || {}
  }

  const openResolveModal = (deliverable: ExpansionDeliverable) => {
    setDeliverableToResolve(deliverable)
    // Default seats to requested amount
    if (!seatsToAdd[deliverable.id]) {
      setSeatsToAdd({ ...seatsToAdd, [deliverable.id]: deliverable.additionalUsers })
    }
    // Default to paid
    if (invoicePaid[deliverable.id] === undefined) {
      setInvoicePaid({ ...invoicePaid, [deliverable.id]: true })
    }
    // Default description
    if (!invoiceDescription[deliverable.id]) {
      setInvoiceDescription({ ...invoiceDescription, [deliverable.id]: `${deliverable.additionalUsers} spots` })
    }
    setShowResolveModal(true)
  }

  const handleResolve = async (deliverableId: number) => {
    const seats = seatsToAdd[deliverableId]
    if (!seats || seats <= 0) {
      toast.error('Please enter a valid number of seats to add')
      return
    }

    const isPaid = invoicePaid[deliverableId] ?? true
    const description = invoiceDescription[deliverableId] || `${seats} spots`

    try {
      setResolving(deliverableId.toString())
      await resolveDeliverable({ 
        deliverableId, 
        seatsToAdd: seats,
        invoicePaid: isPaid,
        invoiceDescription: description
      }).unwrap()
      toast.success(`Successfully resolved deliverable. ${seats} seats added. Invoice ${isPaid ? 'marked as paid' : 'created as pending'}.`)
      setSeatsToAdd({ ...seatsToAdd, [deliverableId]: 0 })
      setShowResolveModal(false)
      setDeliverableToResolve(null)
      refetch()
    } catch (err: any) {
      console.error(err)
      toast.error(err?.data?.message || err?.message || 'Failed to resolve expansion deliverable')
    } finally {
      setResolving(null)
    }
  }

  const formatDate = (dateString?: string) => (dateString ? formatDateTimeEST(dateString) : 'N/A')

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  if (error) {
    console.error('Expansion Deliverables Error:', error)
    return (
      <div className="text-center py-8">
        <p className="text-red-600">Error loading expansion deliverables. Please try again.</p>
        {process.env.NODE_ENV === 'development' && (
          <details className="mt-4 text-left max-w-2xl mx-auto">
            <summary className="cursor-pointer text-sm text-gray-600">Error Details (Dev Only)</summary>
            <pre className="mt-2 p-4 bg-gray-100 rounded text-xs overflow-auto">
              {JSON.stringify(error, null, 2)}
            </pre>
          </details>
        )}
      </div>
    )
  }

  const pendingDeliverables = (deliverables || []).filter(r => r.status === 'pending')
  const resolvedDeliverables = (deliverables || []).filter(r => r.status === 'resolved')

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Expansion Deliverables</h1>
        <p className="text-gray-600">Manage enterprise account expansion deliverables</p>
      </div>

      {/* Pending Deliverables */}
      <div>
        <h2 className="text-xl font-semibold text-gray-900 mb-4">
          Pending Deliverables ({pendingDeliverables.length})
        </h2>

        {pendingDeliverables.length === 0 ? (
          <div className="bg-gray-50 rounded-lg p-8 text-center text-gray-500">
            No pending expansion deliverables
          </div>
        ) : (
          <div className="space-y-4">
            {pendingDeliverables.map((deliverable) => {
              const reasons = parseExpansionReasons(deliverable.expansionReasons)
              const reasonsList = []
              if (reasons.moreEngineers) reasonsList.push('More engineers')
              if (reasons.newTeams) reasonsList.push('New teams')
              if (reasons.executives) reasonsList.push('Executives')
              if (reasons.stakeholders) reasonsList.push('Stakeholders')
              if (reasons.other && deliverable.otherDescription) {
                reasonsList.push(`Other: ${deliverable.otherDescription}`)
              }

              return (
                <div key={deliverable.id} className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
                    <div>
                      <div className="text-sm font-medium text-gray-500">Owner</div>
                      <div className="text-gray-900">{deliverable.user?.name || 'N/A'} ({deliverable.user?.email || 'N/A'})</div>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-gray-500">Organization</div>
                      <div className="text-gray-900">{deliverable.organization?.name || 'N/A'}</div>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-gray-500">Actual Seats</div>
                      <div className="text-gray-900">{deliverable.account?.purchasedSeats || 0}</div>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-gray-500">Used Seats</div>
                      <div className="text-gray-900">—</div>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-gray-500">Requested Users</div>
                      <div className="text-gray-900">{deliverable.additionalUsers}</div>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-gray-500">Deliverable Date</div>
                      <div className="text-gray-900">{formatDate(deliverable.createdAt)}</div>
                    </div>
                  </div>

                  <div className="border-t border-gray-200 pt-4 mt-4 flex gap-4 items-center">
                    <button
                      onClick={() => {
                        setSelectedDeliverable(deliverable)
                        setShowDetailsModal(true)
                      }}
                      className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                    >
                      <EyeIcon className="h-4 w-4 mr-2" />
                      Details
                    </button>
                    <button
                      onClick={() => openResolveModal(deliverable)}
                      className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
                    >
                      Resolve
                    </button>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* Resolved Deliverables */}
      {resolvedDeliverables.length > 0 && (
        <div>
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            Resolved Deliverables ({resolvedDeliverables.length})
          </h2>
          <div className="space-y-4">
            {resolvedDeliverables.map((deliverable) => (
              <div key={deliverable.id} className="bg-gray-50 border border-gray-200 rounded-lg p-4 opacity-75">
                <div className="flex justify-between items-center">
                  <div>
                    <div className="font-medium text-gray-900">
                      {deliverable.organization?.name || 'N/A'} - {deliverable.user?.name || 'N/A'}
                    </div>
                    <div className="text-sm text-gray-500">
                      Resolved on {formatDate(deliverable.resolvedAt)} by {deliverable.resolvedBy?.name || 'N/A'}
                      {deliverable.seatsAdded && ` - ${deliverable.seatsAdded} seats added`}
                    </div>
                  </div>
                  <div className="flex items-center text-green-600">
                    <CheckCircleIcon className="h-5 w-5 mr-1" />
                    Resolved
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Resolve Modal */}
      {showResolveModal && deliverableToResolve && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50" onClick={() => setShowResolveModal(false)}>
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-1/2 lg:w-1/3 shadow-lg rounded-md bg-white" onClick={(e) => e.stopPropagation()}>
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Resolve Expansion Deliverable</h3>
              
              <div className="space-y-4">
                <div className="bg-gray-50 rounded-md p-3">
                  <div className="text-sm text-gray-500">Organization</div>
                  <div className="font-medium">{deliverableToResolve.organization?.name || 'N/A'}</div>
                  <div className="text-sm text-gray-500 mt-2">Owner</div>
                  <div className="font-medium">{deliverableToResolve.user?.name || 'N/A'} ({deliverableToResolve.user?.email || 'N/A'})</div>
                  <div className="text-sm text-gray-500 mt-2">Requested Users</div>
                  <div className="font-medium">{deliverableToResolve.additionalUsers}</div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Seats to Add *
                  </label>
                  <input
                    type="number"
                    value={seatsToAdd[deliverableToResolve.id] || ''}
                    onChange={(e) => setSeatsToAdd({
                      ...seatsToAdd,
                      [deliverableToResolve.id]: parseInt(e.target.value) || 0
                    })}
                    min="1"
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm px-3 py-2 border"
                    placeholder="Number of seats to add"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Invoice Description
                  </label>
                  <input
                    type="text"
                    value={invoiceDescription[deliverableToResolve.id] || ''}
                    onChange={(e) => setInvoiceDescription({
                      ...invoiceDescription,
                      [deliverableToResolve.id]: e.target.value
                    })}
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm px-3 py-2 border"
                    placeholder="e.g., Small team, 5 spots, etc."
                  />
                  <p className="mt-1 text-xs text-gray-500">This text will appear on the invoice instead of "X seats"</p>
                </div>

                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-md">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Invoice Status
                    </label>
                    <p className="text-xs text-gray-500">Mark this invoice as already paid?</p>
                  </div>
                  <div className="flex items-center gap-4">
                    <label className="inline-flex items-center">
                      <input
                        type="radio"
                        name={`invoicePaid-${deliverableToResolve.id}`}
                        checked={invoicePaid[deliverableToResolve.id] === true}
                        onChange={() => setInvoicePaid({ ...invoicePaid, [deliverableToResolve.id]: true })}
                        className="form-radio text-green-600"
                      />
                      <span className="ml-2 text-sm text-green-700">Paid</span>
                    </label>
                    <label className="inline-flex items-center">
                      <input
                        type="radio"
                        name={`invoicePaid-${deliverableToResolve.id}`}
                        checked={invoicePaid[deliverableToResolve.id] === false}
                        onChange={() => setInvoicePaid({ ...invoicePaid, [deliverableToResolve.id]: false })}
                        className="form-radio text-yellow-600"
                      />
                      <span className="ml-2 text-sm text-yellow-700">Pending</span>
                    </label>
                  </div>
                </div>
              </div>

              <div className="mt-6 flex justify-end gap-3">
                <button
                  onClick={() => {
                    setShowResolveModal(false)
                    setDeliverableToResolve(null)
                  }}
                  className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleResolve(deliverableToResolve.id)}
                  disabled={resolving === deliverableToResolve.id.toString() || !seatsToAdd[deliverableToResolve.id] || seatsToAdd[deliverableToResolve.id] <= 0}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  {resolving === deliverableToResolve.id.toString() ? 'Resolving...' : 'Resolve & Create Invoice'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Details Modal */}
      {showDetailsModal && selectedDeliverable && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50" onClick={() => setShowDetailsModal(false)}>
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white" onClick={(e) => e.stopPropagation()}>
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Expansion Deliverable Details</h3>
              
              <div className="space-y-4">
                <div>
                  <div className="text-sm font-medium text-gray-500">Owner</div>
                  <div className="text-gray-900">{selectedDeliverable.user?.name || 'N/A'} ({selectedDeliverable.user?.email || 'N/A'})</div>
                </div>

                <div>
                  <div className="text-sm font-medium text-gray-500">Organization</div>
                  <div className="text-gray-900">{selectedDeliverable.organization?.name || 'N/A'}</div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-sm font-medium text-gray-500">Actual Seats</div>
                    <div className="text-gray-900">{selectedDeliverable.account?.purchasedSeats || 0}</div>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-gray-500">Requested Additional Users</div>
                    <div className="text-gray-900">{selectedDeliverable.additionalUsers}</div>
                  </div>
                </div>

                <div>
                  <div className="text-sm font-medium text-gray-500 mb-2">Which Areas Are Expanding?</div>
                  {(() => {
                    const reasons = parseExpansionReasons(selectedDeliverable.expansionReasons)
                    const reasonsList = []
                    if (reasons.moreEngineers) reasonsList.push('More engineers working on active deliverables')
                    if (reasons.newTeams) reasonsList.push('New team(s) joining Motionode')
                    if (reasons.executives) reasonsList.push('Executives / leadership visibility')
                    if (reasons.stakeholders) reasonsList.push('Clients / stakeholders as observers')
                    if (reasons.other && selectedDeliverable.otherDescription) {
                      reasonsList.push(`Other: ${selectedDeliverable.otherDescription}`)
                    }

                    return reasonsList.length > 0 ? (
                      <ul className="list-disc list-inside space-y-1 text-gray-900">
                        {reasonsList.map((reason, idx) => (
                          <li key={idx}>{reason}</li>
                        ))}
                      </ul>
                    ) : (
                      <div className="text-gray-500">None selected</div>
                    )
                  })()}
                </div>

                {selectedDeliverable.otherDescription && (
                  <div>
                    <div className="text-sm font-medium text-gray-500 mb-2">Other Description</div>
                    <div className="bg-gray-50 rounded-md p-3 text-gray-900 whitespace-pre-wrap">
                      {selectedDeliverable.otherDescription}
                    </div>
                  </div>
                )}

                <div>
                  <div className="text-sm font-medium text-gray-500">Deliverable Date</div>
                  <div className="text-gray-900">{formatDate(selectedDeliverable.createdAt)}</div>
                </div>
              </div>

              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ExpansionDeliverables
